import React, { useCallback, useMemo, useState } from 'react'
import styled from 'styled-components'
import config from 'constants/config'
import moment from 'moment'
import LineCalendarIcon from 'components/Luxkit/Icons/line/LineCalendarIcon'
import LineAwardIcon from 'components/Luxkit/Icons/line/LineAwardIcon'
import LineGiftIcon from 'components/Luxkit/Icons/line/LineGiftIcon'
import { rem } from 'polished'
import USPModal from './USPModal'
import useIsAvailableForPurchase from '../hooks/useIsAvailableForPurchase'
import FlashCancellationPolicies from 'components/Common/CancellationPolicies/Flash/FlashCancellationPolicies'
import TextLink from 'components/Luxkit/TextLink'
import { INTL_DMY_CASUAL_FORMAT } from 'constants/dateFormats'
import { mediaQueryUp } from 'components/utils/breakpoint'
import Caption from 'components/Luxkit/Typography/Caption'
import { useTranslation } from 'react-i18next'

const USPContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${rem(16)};

  ${mediaQueryUp.xs} {
    display: grid;
    grid-template-columns: minmax(min-content, max-content) minmax(min-content, max-content);
    align-items: flex-start;
  }
`

const USPBodyText = styled(Caption)`
  margin-left: ${rem(24)};
`

interface Props {
  offer: App.Offer;
  checkIn?: moment.Moment;
}

type ModalTypes = 'bnbl' | 'bestPrice' | 'gifting'

function FlashHotelUSPs(props: Props) {
  const { offer, checkIn } = props
  const [modalType, setModalType] = useState<ModalTypes>()
  const [open, setOpen] = useState(false)
  const availableForPurchase = useIsAvailableForPurchase()

  const { t } = useTranslation(['offerPage', 'hotelOffer'])

  const onItemClick = useCallback((e: React.MouseEvent<HTMLElement>) => {
    const modalType = e.currentTarget.dataset.type as ModalTypes
    setModalType(modalType)
    setOpen(true)
  }, [])

  const onClose = useCallback(() => {
    setOpen(false)
  }, [])

  const hasBNBL = offer.hasBuyNowBookLaterPackages && offer.hasBuyNowBookLater && offer.travelToDate && config.accommodation.hasBnbl
  const isSevenDaysBeforeTravelToDate = moment().add(1, 'w').isBefore(offer.travelToDate)

  const bookBy = useMemo(() => moment(offer.bookByDate), [offer.bookByDate])
  const travelUntil = useMemo(() => moment(offer.travelToDate), [offer.travelToDate])

  return (
    <>
      <USPContainer>
        {availableForPurchase && <FlashCancellationPolicies
          checkInDate={checkIn}
          propertyTimezoneOffset={offer?.property?.timezoneOffset}
          cancellationPolicy={offer?.lowestPricePackage?.roomRate?.cancellationPolicy}
          isFlashUSP
        />}
        {!offer.bundledWithFlightsOnly && config.bestPriceGuarantee && !offer.disableBestPriceGuarantee && (
          <TextLink
            size="medium"
            variant="highlight"
            underline={false}
            data-type="bestPrice"
            onClick={onItemClick}
            startIcon={<LineAwardIcon />}
          >
            {t('Best price guarantee', { ns: 'offerPage' })}
          </TextLink>
        )}
        {hasBNBL && <div>
          <TextLink
            size="medium"
            variant="highlight"
            underline={false}
            data-type="bnbl"
            data-testid="usp-bnbl"
            onClick={onItemClick}
            startIcon={<LineCalendarIcon />}
          >
            {t('Buy now, choose dates later', { ns: 'offerPage' })}
          </TextLink>
          <USPBodyText colour="neutral-three" variant="medium">
            {t('travelUntilDate', { ns: 'hotelOffer', travelToDate: travelUntil, ...INTL_DMY_CASUAL_FORMAT })}
          </USPBodyText>
        </div>}
        {hasBNBL && isSevenDaysBeforeTravelToDate &&
          <TextLink
            size="medium"
            variant="highlight"
            underline={false}
            data-type="gifting"
            onClick={onItemClick}
            startIcon={<LineGiftIcon />}
          >
            {t('giftingAvailable', { ns: 'offerPage' })}
          </TextLink>
        }
      </USPContainer>
      <USPModal open={open} onClose={onClose} modalType={modalType} bookBy={bookBy} travelUntil={travelUntil} />
    </>
  )
}

export default React.memo(FlashHotelUSPs)
